import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../components/BottomNavigation';
import { ProductReviewsDrawer } from '../components/ui/ProductReviewsDrawer';
import { 
  Search, 
  Location, 
  Filter, 
  Heart, 
  Buy,
  Star,
  User
} from 'react-iconly';
import { useCart } from '../contexts/CartContext';
import { getProductsForFoodDiscovery, getProductsByDietaryRestrictions, getProductsByPriceRange } from '../utils/productApi';
import { getProductReviews } from '../lib/reviewsService';
import { useAuth } from '../contexts/AuthContext';
import { getUserLocation, getCityStateFromCoords } from '../utils/distanceUtils';

// Predefined allergen list for ingredient parsing
const MAJOR_ALLERGENS = [
  // Grains
  'wheat', 'gluten', 'rye', 'barley', 'oats', 'spelt', 'kamut', 'triticale',
  // Dairy
  'milk', 'dairy', 'butter', 'cheese', 'cream', 'yogurt', 'casein', 'whey', 'lactose', 'ghee', 'kefir', 'sour cream',
  // Eggs
  'egg', 'eggs', 'albumin', 'ovalbumin', 'egg white', 'egg yolk',
  // Nuts
  'peanut', 'peanuts', 'almond', 'almonds', 'walnut', 'walnuts', 'cashew', 'cashews', 'pistachio', 'pistachios', 'hazelnut', 'hazelnuts', 'pecan', 'pecans', 'macadamia', 'macadamias', 'brazil nut', 'brazil nuts', 'pine nut', 'pine nuts', 'chestnut', 'chestnuts',
  // Soy
  'soy', 'soya', 'soybean', 'soybeans', 'tofu', 'tempeh', 'miso', 'edamame', 'soy sauce', 'soy milk',
  // Fish & Shellfish
  'fish', 'salmon', 'tuna', 'cod', 'halibut', 'shellfish', 'shrimp', 'prawn', 'crab', 'lobster', 'clam', 'mussel', 'oyster', 'scallop', 'anchovy', 'sardine', 'mackerel',
  // Other common allergens
  'sesame', 'sesame seed', 'sesame seeds', 'mustard', 'celery', 'sulfite', 'sulfites', 'lupin', 'molluscs'
];

// Additional ingredient-to-allergen mappings for common ingredients
const INGREDIENT_ALLERGEN_MAPPINGS = {
  'flour': ['wheat', 'gluten'],
  'bread flour': ['wheat', 'gluten'],
  'all-purpose flour': ['wheat', 'gluten'],
  'whole wheat flour': ['wheat', 'gluten'],
  'rye flour': ['rye', 'gluten'],
  'barley flour': ['barley', 'gluten'],
  'oat flour': ['oats', 'gluten'],
  'bread': ['wheat', 'gluten'],
  'pasta': ['wheat', 'gluten'],
  'noodles': ['wheat', 'gluten'],
  'soy sauce': ['soy', 'wheat'],
  'teriyaki sauce': ['soy', 'wheat'],
  'worcestershire sauce': ['fish', 'wheat'],
  'malt': ['barley', 'gluten'],
  'malt extract': ['barley', 'gluten'],
  'malt vinegar': ['barley', 'gluten'],
  'beer': ['barley', 'gluten'],
  'ale': ['barley', 'gluten'],
  'lager': ['barley', 'gluten'],
  'stout': ['barley', 'gluten'],
  'wine': ['sulfites'],
  'red wine': ['sulfites'],
  'white wine': ['sulfites']
};

// Function to detect allergens from ingredients
const detectAllergens = (ingredients, existingAllergens = []) => {
  const detectedAllergens = [];
  
  // Start with existing allergens if available
  if (Array.isArray(existingAllergens) && existingAllergens.length > 0) {
    existingAllergens.forEach(allergen => {
      const allergenName = typeof allergen === 'string' ? allergen : allergen.name || allergen.text || 'Unknown';
      if (!detectedAllergens.includes(allergenName)) {
        detectedAllergens.push(allergenName);
      }
    });
  }
  
  // Parse ingredients to detect additional allergens
  if (Array.isArray(ingredients) && ingredients.length > 0) {
    
    ingredients.forEach(ingredient => {
      let ingredientText = '';
      
      // Handle different ingredient formats
      if (typeof ingredient === 'string') {
        ingredientText = ingredient.toLowerCase();
      } else if (ingredient && typeof ingredient === 'object') {
        ingredientText = (ingredient.name || ingredient.text || '').toLowerCase();
      }
      
      if (!ingredientText) return;
      
      // First check ingredient-to-allergen mappings
      Object.keys(INGREDIENT_ALLERGEN_MAPPINGS).forEach(ingredientKey => {
        if (ingredientText.includes(ingredientKey.toLowerCase())) {
          INGREDIENT_ALLERGEN_MAPPINGS[ingredientKey].forEach(allergen => {
            const displayName = allergen.charAt(0).toUpperCase() + allergen.slice(1);
            if (!detectedAllergens.includes(displayName)) {
              detectedAllergens.push(displayName);
            }
          });
        }
      });
      
      // Then check against general allergen list with more flexible matching
      MAJOR_ALLERGENS.forEach(allergen => {
        const allergenLower = allergen.toLowerCase();
        
        // Check for exact match or word boundary match
        if (ingredientText.includes(allergenLower) || 
            ingredientText.split(/\s+/).some(word => word === allergenLower)) {
          // Capitalize first letter for display
          const displayName = allergen.charAt(0).toUpperCase() + allergen.slice(1);
          if (!detectedAllergens.includes(displayName)) {
            detectedAllergens.push(displayName);
          }
        }
      });
    });
  }
  
  // Sort allergens alphabetically for consistent display
  detectedAllergens.sort();
  
  if (detectedAllergens.length === 0) {
    if (!Array.isArray(ingredients) || ingredients.length === 0) {
      return { allergens: [], status: 'unavailable' };
    }
    return { allergens: [], status: 'none' };
  }
  
  return {
    allergens: detectedAllergens,
    status: 'contains'
  };
};

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const ORANGE_ACCENT = '#f4a261';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

// Helper functions to calculate date ranges
const getDateRangeForPeriod = (period, customDate = null) => {
  const today = new Date();
  const todayString = today.toISOString().split('T')[0];
  
  switch (period) {
    case 'today':
      return { startDate: todayString, endDate: todayString };
    
    case 'this-week':
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week (Saturday)
      return {
        startDate: startOfWeek.toISOString().split('T')[0],
        endDate: endOfWeek.toISOString().split('T')[0]
      };
    
    case 'next-week':
      const startOfNextWeek = new Date(today);
      startOfNextWeek.setDate(today.getDate() + (7 - today.getDay())); // Next Sunday
      const endOfNextWeek = new Date(startOfNextWeek);
      endOfNextWeek.setDate(startOfNextWeek.getDate() + 6); // Next Saturday
      return {
        startDate: startOfNextWeek.toISOString().split('T')[0],
        endDate: endOfNextWeek.toISOString().split('T')[0]
      };
    
    case 'this-month':
      // "This month" means within the next 30 days from today
      const thirtyDaysFromToday = new Date(today);
      thirtyDaysFromToday.setDate(today.getDate() + 30);
      const endDateString = thirtyDaysFromToday.toISOString().split('T')[0];
      return {
        startDate: todayString,
        endDate: endDateString
      };
    
    case 'custom':
      return { startDate: customDate || todayString, endDate: customDate || todayString };
    
    default:
      return { startDate: todayString, endDate: todayString };
  }
};

const getDisplayTextForPeriod = (period, customDate = null) => {
  switch (period) {
    case 'today':
      return 'Today';
    case 'this-week':
      return 'This Week';
    case 'next-week':
      return 'Next Week';
    case 'this-month':
      return 'This Month';
    case 'custom':
      return customDate ? new Date(customDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Today';
    case null:
      return 'Any Date';
    default:
      return 'Any Date';
  }
};

// Updated categories to match the design
const categories = [
  { id: 'ready-to-eat', name: 'Ready to Eat', icon: '⚡', color: ORANGE_ACCENT },
  { id: 'meal-prep', name: 'Meal Prep', icon: '🥗', color: '#4CAF50' },
  { id: 'bake-home', name: 'Bake at Home', icon: '🍰', color: '#FF9800' }
];

const cuisines = [
  'Mexican', 'Indian', 'Italian', 'Chinese', 'Japanese', 'Thai', 
  'Mediterranean', 'American', 'French', 'Korean', 'Vietnamese', 'Greek'
];

const dietaryTags = [
  'Vegan', 'Vegetarian', 'Gluten-Free', 'Keto', 'Nut-Free', 
  'Dairy-Free', 'Low-Carb', 'Paleo', 'Halal', 'Kosher'
];

const availabilityOptions = [
  'Ready Now (within 1 hour)',
  'Preorder',
  'Pickup window'
];

const sortOptions = [
  { id: 'price-low', name: 'Price: Low to High' },
  { id: 'price-high', name: 'Price: High to Low' },
  { id: 'rating', name: 'Highest Rated' },
  { id: 'distance', name: 'Nearest First' }
];

// Fallback dummy food items
const fallbackFoodItems = [
  {
    id: 1,
    name: 'Handmade Pasta Carbonara',
    vendor: 'Chef Mario\'s Kitchen',
    price: 18.99,
    rating: 4.8,
    reviews: 124,
    image: 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop',
    imageUrl: 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop',
    category: 'ready-now',
    cuisine: 'Italian',
    dietary: ['Vegetarian'],
    availability: 'Ready Now (within 1 hour)',
    distance: 0.8
  },
  {
    id: 2,
    name: 'Vegan Buddha Bowl',
    vendor: 'Green Eats',
    price: 14.50,
    rating: 4.6,
    reviews: 89,
    image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop',
    imageUrl: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop',
    category: 'ready-now',
    cuisine: 'Mediterranean',
    dietary: ['Vegan', 'Gluten-Free'],
    availability: 'Ready Now (within 1 hour)',
    distance: 1.2
  },
  {
    id: 3,
    name: 'Chocolate Chip Cookies',
    vendor: 'Sweet Dreams Bakery',
    price: 12.99,
    rating: 4.9,
    reviews: 203,
    image: 'https://images.unsplash.com/photo-1499636136210-6f4ee915583e?w=400&h=300&fit=crop',
    imageUrl: 'https://images.unsplash.com/photo-1499636136210-6f4ee915583e?w=400&h=300&fit=crop',
    category: 'bake-home',
    cuisine: 'American',
    dietary: ['Vegetarian'],
    availability: 'Pickup window',
    distance: 2.1
  },
  {
    id: 4,
    name: 'Chicken Tikka Masala',
    vendor: 'Spice Route',
    price: 16.99,
    rating: 4.7,
    reviews: 156,
    image: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=400&h=300&fit=crop',
    imageUrl: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=400&h=300&fit=crop',
    category: 'ready-now',
    cuisine: 'Indian',
    dietary: ['Gluten-Free'],
    availability: 'Ready Now (within 1 hour)',
    distance: 1.5
  },
  {
    id: 5,
    name: 'Meal Prep Protein Bowl',
    vendor: 'Fit Fuel',
    price: 22.99,
    rating: 4.5,
    reviews: 67,
    image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop',
    imageUrl: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop',
    category: 'meal-prep',
    cuisine: 'American',
    dietary: ['Keto', 'Low-Carb'],
    availability: 'Preorder',
    distance: 3.2
  }
];

export function FoodBrowsing() {
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ready-now');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedDietary, setSelectedDietary] = useState([]);
  const [selectedAvailability, setSelectedAvailability] = useState([]);
  const [sortBy, setSortBy] = useState('rating');
  const [favorites, setFavorites] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
  const [selectedEndDate, setSelectedEndDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('today'); // 'today', 'this-week', 'next-week', 'this-month', 'custom'
  const navigate = useNavigate();
  const { addCartItem, cartItems, updateCartItem, removeCartItem } = useCart();
  const [addStatus, setAddStatus] = useState({}); // { [itemId]: 'idle' | 'adding' | 'added' | 'error' }
  
  // Variant selection state
  const [showVariantSheet, setShowVariantSheet] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variantSelections, setVariantSelections] = useState({});
  const [quantity, setQuantity] = useState(1);
  
  // Reviews drawer state
  const [showReviewsDrawer, setShowReviewsDrawer] = useState(false);
  const [selectedProductForReviews, setSelectedProductForReviews] = useState(null);
  
  // Distance filtering state
  const [foodItems, setFoodItems] = useState(fallbackFoodItems);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterInfo, setFilterInfo] = useState({
    userLocation: null,
    distancePreference: 25,
    totalFound: 0
  });

  const [locationDisplay, setLocationDisplay] = useState('San Francisco, CA');
  
  // Auto-play state
  const [currentPlayingIndex, setCurrentPlayingIndex] = useState(0);
  const [videoRefs, setVideoRefs] = useState([]);

  // Fetch products using optimized food discovery API
  useEffect(() => {
    let cancelled = false;
    async function fetchProductsOptimized() {
      if (!currentUser?.uid) {
        setFoodItems(fallbackFoodItems);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        // Use new optimized API with searchableFields for 10-100x faster queries
        let products = await getProductsForFoodDiscovery({
          // Add any specific filters here
          // availableNow: true, // Uncomment to show only products available now
          // listingType: 'meal_prep', // Uncomment to filter by listing type
          // itemCategory: 'main_dish', // Uncomment to filter by category
        });
        
        // If we get very few products, fall back to traditional query
        if (products.length < 3) {
          console.log('🔍 Few products returned from optimized query, falling back to traditional query');
          const { getDocs, collection, query, where } = await import('firebase/firestore');
          const { db } = await import('../firebase');
          
          // Use traditional query without searchableFields
          const productsCollection = collection(db, 'products');
          const q = query(productsCollection, where('status', '==', true));
          const snapshot = await getDocs(q);
          
          const allProducts = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Filter products using the traditional filterAvailableProducts function
          const { filterAvailableProducts } = await import('../utils/productApi');
          products = filterAvailableProducts(allProducts);
          
          console.log(`🔍 Traditional query returned ${products.length} available products`);
        }
        
        // Debug: Log the raw product data from Firestore
        // Raw products from Firestore
        if (products.length > 0) {
                      // Sample product data available
        } else {
                      // No products returned from API, will use fallback items
        }
        
        // Convert products to food item format for the browsing page with real review data
        const foodProducts = await Promise.all(products.map(async product => {
          // Apply allergen detection to ingredients
          const allergenDetection = detectAllergens(product.ingredients || [], product.allergens || []);
          const detectedAllergens = allergenDetection.allergens;
          
          // Fetch real review data for this product
          let rating = 0; // Default rating (0 when no reviews, matching FoodDiscovery and Storefront)
          let reviewCount = 0; // Default review count
          
          try {
            console.log('🔍 Fetching reviews for product:', product.id, product.name);
            const reviews = await getProductReviews(product.id, 50); // Get up to 50 reviews
            console.log('🔍 Reviews fetched for product:', product.id, 'count:', reviews.length);
            
            if (reviews.length > 0) {
              // Calculate average rating
              const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
              rating = Math.round((totalRating / reviews.length) * 10) / 10; // Round to 1 decimal
              reviewCount = reviews.length;
              console.log('🔍 Calculated rating for product:', product.id, 'rating:', rating, 'count:', reviewCount);
            } else {
              console.log('🔍 No reviews found for product:', product.id);
              // Keep rating at 0 and reviewCount at 0 when no reviews
            }
          } catch (error) {
            console.warn('⚠️ Error fetching reviews for product:', product.id, error);
            // Keep default values (rating: 0, reviewCount: 0)
          }
          
          return {
            id: product.id,
            name: product.name,
            vendor: product.shopName || product.vendorName || '',
            price: Number(product.price) || 0,
            rating: rating,
            reviews: reviewCount,
            image: product.imageUrl || 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop',
            imageUrl: product.imageUrl || 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop',
            videoUrl: product.videoUrl || null, // <-- Add videoUrl field
            category: product.category || 'ready-now',
            cuisine: product.cuisine || 'American',
            dietary: product.dietary || [],
            allergens: detectedAllergens, // Add detected allergens
            ingredients: product.ingredients || [], // Include ingredients for allergen detection
            availability: product.availability || 'Ready Now (within 1 hour)',
            distance: product.distance,
            status: product.status, // <-- Ensure status is included
            sellerId: product.sellerId, // <-- Include sellerId for discount validation
            inventory: product.inventory || null, // <-- Include inventory data for availability calculation
            variants: product.variants || null, // <-- Include variants for variant selection
            variations: product.variations || null, // <-- Include variations for variant selection
            sizes: product.sizes || null, // <-- Include sizes for variant selection
            spiceLevels: product.spiceLevels || null // <-- Include spiceLevels for variant selection
          };
        }));

        setFoodItems(foodProducts.length > 0 ? foodProducts : fallbackFoodItems);
        setFilterInfo({
          userLocation: null, // Will be updated when location is available
          distancePreference: 25, // Default distance
          totalFound: products.length
        });
        
      } catch (err) {
        console.error('Error fetching products with optimized API:', err);
        setError('Failed to load products. Showing all available options.');
        setFoodItems(fallbackFoodItems);
      } finally {
        setLoading(false);
      }
    }

    fetchProductsOptimized();
  }, [currentUser]);



  useEffect(() => {
    let cancelled = false;
    async function detectAndSetLocation() {
      setLocationDisplay('Detecting location...');
      const coords = await getUserLocation();
              // Detected user coordinates
      if (!coords) {
        setLocationDisplay('San Francisco, CA'); // fallback
                  // Using fallback location: San Francisco, CA
        return;
      }
      const cityState = await getCityStateFromCoords(coords.lat, coords.lng);
      if (!cancelled) {
        setLocationDisplay(cityState);
        // Reverse geocoded location
      }
    }
    detectAndSetLocation();
    return () => { cancelled = true; };
  }, []);

  // Log all products fetched from Firestore before filtering
  useEffect(() => {
    if (foodItems && foodItems.length > 0) {
      // All products fetched
    } else {
              // No products fetched from Firestore
    }
  }, [foodItems]);

  // Filter and sort food items
  const filteredFoodItems = foodItems
    .filter(item => {
      // Category filter
      if (selectedCategory === 'ready-now') {
        if (!item.status) return false;
      } else if (selectedCategory && item.category !== selectedCategory) {
        return false;
      }
      // If no category is selected, show all items
      // Search filter
      if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
          !(item.vendor && item.vendor.toLowerCase().includes(searchQuery.toLowerCase()))) {
        return false;
      }
      // Cuisine filter
      if (selectedCuisines.length > 0 && !selectedCuisines.includes(item.cuisine)) {
        return false;
      }
      // Dietary filter
      if (selectedDietary.length > 0 && !selectedDietary.some(diet => item.dietary.includes(diet))) {
        return false;
      }
      // Availability filter
      if (selectedAvailability.length > 0 && !selectedAvailability.includes(item.availability)) {
        return false;
      }
      // Date filter - check if product is available on selected time period
      if (selectedTimePeriod && item.inventory) {
        const inventory = item.inventory;
        const { startDate, endDate } = getDateRangeForPeriod(selectedTimePeriod, selectedDate);
        
        // Check if product is available on ANY date in the selected period
        const hasAvailableInPeriod = inventory.scheduled?.some(row => {
          if (!row.enabled || row.available_total <= 0) return false;
          
          // Parse date strings directly to avoid timezone issues
          const [rowYear, rowMonth, rowDay] = row.service_date.split('-').map(Number);
          const rowDate = new Date(rowYear, rowMonth - 1, rowDay, 12, 0, 0, 0);
          
          const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
          const periodStart = new Date(startYear, startMonth - 1, startDay, 12, 0, 0, 0);
          
          const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
          const periodEnd = new Date(endYear, endMonth - 1, endDay, 12, 0, 0, 0);
          
          return rowDate >= periodStart && rowDate <= periodEnd;
        });
        
        // If no inventory found in selected period, filter out the product
        if (!hasAvailableInPeriod) {
          return false;
        }
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'rating':
          return b.rating - a.rating;
        case 'distance':
          return (a.distance || 0) - (b.distance || 0);
        default:
          return 0;
      }
    });

  // Auto-switch to 'this-month' if no products found for 'today'
  useEffect(() => {
    if (!loading && !error && filteredFoodItems.length === 0 && selectedTimePeriod === 'today') {
      console.log('🔍 No products found for today, switching to this-month filter');
      setSelectedTimePeriod('this-month');
    }
  }, [filteredFoodItems.length, selectedTimePeriod, loading, error]);

  const toggleFavorite = (itemId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(itemId)) {
      newFavorites.delete(itemId);
    } else {
      newFavorites.add(itemId);
    }
    setFavorites(newFavorites);
  };

  const handleProductClick = (product) => {
    // Debug: Log product data to see what we're working with
    console.log('[FoodBrowsing] Product clicked:', {
      id: product.id,
      name: product.name,
      variants: product.variants,
      variations: product.variations,
      sizes: product.sizes,
      spiceLevels: product.spiceLevels,
      hasVariants: product.variants && product.variants.length > 0,
      hasVariations: product.variations && product.variations.length > 0,
      hasSizes: product.sizes && product.sizes.length > 0,
      hasSpiceLevels: product.spiceLevels && product.spiceLevels.length > 0
    });
    
    // Navigate to product detail page with the current filtered products list
    // Removed autoOpen=true - just show the video without auto-opening the sheet
    const url = `/products/${product.id}`;
    console.log('[FoodBrowsing] Navigating to product detail without auto-open');
    
    navigate(url, {
      state: {
        fromFoodBrowsing: true,
        productsList: filteredFoodItems,
        currentProductIndex: filteredFoodItems.findIndex(item => item.id === product.id)
      }
    });
  };

  // Initialize video refs and start auto-play when food items change
  useEffect(() => {
    if (filteredFoodItems.length > 0) {
      // Find the first item with a video URL
      const firstVideoIndex = filteredFoodItems.findIndex(item => item.videoUrl);
      const startIndex = firstVideoIndex !== -1 ? firstVideoIndex : 0;
      
              // Initializing auto-play
      
      setCurrentPlayingIndex(startIndex);
      setVideoRefs(new Array(filteredFoodItems.length).fill(null));
    }
  }, [filteredFoodItems.length]);

  // Single useEffect to handle all video playback logic
  useEffect(() => {
            // Auto-play effect triggered
    
    if (filteredFoodItems.length === 0 || videoRefs.length === 0) {
              // Auto-play effect early return - missing data
      return;
    }

    const currentVideo = videoRefs[currentPlayingIndex];
    const currentItem = filteredFoodItems[currentPlayingIndex];
    
    // Skip videos without URLs and advance to the next one
    if (!currentVideo || !currentItem || !currentItem.videoUrl) {
                // Skipping video - no video URL, advancing to next
      const nextIndex = (currentPlayingIndex + 1) % filteredFoodItems.length;
      setCurrentPlayingIndex(nextIndex);
      return;
    }

    // Set up event listener for when video ends
    const handleVideoEnd = () => {
              // Video ended, moving to next
      const nextIndex = (currentPlayingIndex + 1) % filteredFoodItems.length;
      setCurrentPlayingIndex(nextIndex);
    };

    const handleVideoError = (error) => {
      // Video error occurred
      // Move to next video on error
      const nextIndex = (currentPlayingIndex + 1) % filteredFoodItems.length;
      setCurrentPlayingIndex(nextIndex);
    };

    // Add event listeners
    currentVideo.addEventListener('ended', handleVideoEnd);
    currentVideo.addEventListener('error', handleVideoError);

    // Try to play the current video with proper error handling
    const playVideo = async () => {
      try {
        // Attempting to play video
        
        // Check if video is already playing to prevent interruption
        if (currentVideo.readyState >= 2 && !currentVideo.paused) {
          // Video is already playing
          return; // Video is already playing
        }
        
        // Reset video to beginning
        currentVideo.currentTime = 0;
        
        // Wait a bit to ensure video is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Only play if not already playing
        if (currentVideo.paused) {
          // Playing video
          await currentVideo.play();
                      // Video started playing
        }
      } catch (error) {
                  // Autoplay prevented or failed for video
        // Move to next video if autoplay fails
        const nextIndex = (currentPlayingIndex + 1) % filteredFoodItems.length;
        setCurrentPlayingIndex(nextIndex);
      }
    };

    // Start playing after a short delay to ensure refs are stable
    const playTimer = setTimeout(playVideo, 200);

    return () => {
      clearTimeout(playTimer);
      currentVideo.removeEventListener('ended', handleVideoEnd);
      currentVideo.removeEventListener('error', handleVideoError);
    };
  }, [currentPlayingIndex, filteredFoodItems.length, videoRefs]);

  // Add to cart handler
  const handleAddToCart = async (item) => {
    // Debug: Log the full item data to see what we're working with
    console.log('[FoodBrowsing] Quick add clicked for item:', {
      id: item.id,
      name: item.name,
      variants: item.variants,
      variations: item.variations,
      sizes: item.sizes,
      spiceLevels: item.spiceLevels,
      hasVariants: item.variants && item.variants.length > 0,
      hasVariations: item.variations && item.variations.length > 0,
      hasSizes: item.sizes && item.sizes.length > 0,
      hasSpiceLevels: item.spiceLevels && item.spiceLevels.length > 0,
      allKeys: Object.keys(item)
    });
    
    // Check if product has variants - check multiple possible field names
    const hasVariants = (item.variants && item.variants.length > 0) ||
                       (item.variations && item.variations.length > 0) ||
                       (item.sizes && item.sizes.length > 0) ||
                       (item.spiceLevels && item.spiceLevels.length > 0);
    
    if (hasVariants) {
      console.log('[FoodBrowsing] Product has variants, opening variant selection sheet');
      // Open variant selection sheet instead of navigating
      setSelectedProduct(item);
      setVariantSelections({});
      setQuantity(1);
      setShowVariantSheet(true);
      return;
    } else {
      console.log('[FoodBrowsing] Product has no variants, proceeding with direct add to cart');
    }
    
    // If no variants, proceed with normal add to cart
    setAddStatus(s => ({ ...s, [item.id]: 'adding' }));
    try {
      // Calculate next available fulfillment day for this product
      let fulfillmentDay = null;
      if (item.inventory?.scheduled && Array.isArray(item.inventory.scheduled)) {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        const policy = item.policy;
        
        const nextAvailableDay = item.inventory.scheduled
          .filter(row => {
            // Check if date has available units
            if (row.available_total <= 0) return false;
            
            // Check if date is in the future
            if (row.service_date <= todayString) return false;
            
            // Check policy restrictions
            if (policy) {
              const orderDate = new Date(row.service_date);
              const daysDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
              
              // Check min_order_ahead policy
              if (policy.min_order_ahead && policy.min_order_ahead.value) {
                const minDays = policy.min_order_ahead.value;
                if (daysDiff < minDays) return false;
              }
              
              // Check same_day_allowed policy
              if (!policy.same_day_allowed && daysDiff === 0) return false;
            }
            
            return true;
          })
          .sort((a, b) => new Date(a.service_date) - new Date(b.service_date))
          .find(row => new Date(row.service_date) > today);
        
        if (nextAvailableDay) {
          fulfillmentDay = nextAvailableDay.service_date;
        }
      }
      
      await addCartItem({
        id: item.id,
        name: item.name,
        image: item.imageUrl || item.image, // Always prefer imageUrl if available
        imageUrl: item.imageUrl || item.image, // Always set imageUrl for cart
        price: item.price,
        qty: 1,
        fulfillmentDay: fulfillmentDay, // Add the calculated fulfillment day
        attributes: item.dietary?.join(', '),
        sellerId: item.sellerId, // Include sellerId for discount validation
        category: item.category // Include category for discount scope validation
      });
      setAddStatus(s => ({ ...s, [item.id]: 'added' }));
      setTimeout(() => setAddStatus(s => ({ ...s, [item.id]: 'idle' })), 1200);
    } catch (err) {
      setAddStatus(s => ({ ...s, [item.id]: 'error' }));
      setTimeout(() => setAddStatus(s => ({ ...s, [item.id]: 'idle' })), 2000);
    }
  };

  // Variant selection functions
  const handleVariantSelection = (variantKey, optionValue) => {
    setVariantSelections(prev => ({
      ...prev,
      [variantKey]: optionValue
    }));
  };

  const allVariantsSelected = () => {
    if (!selectedProduct) return false;
    
    const variants = selectedProduct.variants || selectedProduct.variations || selectedProduct.sizes || selectedProduct.spiceLevels || [];
    return variants.every(variant => variantSelections[variant.key || variant.name] !== undefined);
  };

  const handleAddToCartWithVariants = async () => {
    if (!selectedProduct || !allVariantsSelected()) return;

    try {
      setAddStatus(s => ({ ...s, [selectedProduct.id]: 'adding' }));
      
      // Calculate next available fulfillment day for this product
      let fulfillmentDay = null;
      if (selectedProduct.inventory?.scheduled && Array.isArray(selectedProduct.inventory.scheduled)) {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        const policy = selectedProduct.policy;
        
        const nextAvailableDay = selectedProduct.inventory.scheduled
          .filter(row => {
            if (row.available_total <= 0) return false;
            if (row.service_date <= todayString) return false;
            
            if (policy) {
              const orderDate = new Date(row.service_date);
              const daysDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
              
              if (policy.min_order_ahead && policy.min_order_ahead.value) {
                const minDays = policy.min_order_ahead.value;
                if (daysDiff < minDays) return false;
              }
              
              if (!policy.same_day_allowed && daysDiff === 0) return false;
            }
            
            return true;
          })
          .sort((a, b) => new Date(a.service_date) - new Date(b.service_date))
          .find(row => new Date(row.service_date) > today);
        
        if (nextAvailableDay) {
          fulfillmentDay = nextAvailableDay.service_date;
        }
      }

      // Create unique ID that includes variant information
      const variantString = Object.keys(variantSelections).length > 0 
        ? Object.entries(variantSelections)
            .map(([key, value]) => `${key}:${value}`)
            .sort()
            .join('|')
        : '';
      const uniqueId = variantString ? `${selectedProduct.id}_${variantString}` : selectedProduct.id;

      await addCartItem({
        id: uniqueId,
        productId: selectedProduct.id,
        name: selectedProduct.name,
        image: selectedProduct.imageUrl || selectedProduct.image,
        imageUrl: selectedProduct.imageUrl || selectedProduct.image,
        price: selectedProduct.price,
        qty: quantity,
        fulfillmentDay: fulfillmentDay,
        attributes: selectedProduct.dietary?.join(', '),
        sellerId: selectedProduct.sellerId,
        category: selectedProduct.category,
        variant: Object.keys(variantSelections).length > 0 ? variantSelections : null
      });

      setAddStatus(s => ({ ...s, [selectedProduct.id]: 'added' }));
      setTimeout(() => setAddStatus(s => ({ ...s, [selectedProduct.id]: 'idle' })), 1200);
      
      // Close the sheet
      setShowVariantSheet(false);
      setSelectedProduct(null);
      setVariantSelections({});
      setQuantity(1);
      
    } catch (err) {
      setAddStatus(s => ({ ...s, [selectedProduct.id]: 'error' }));
      setTimeout(() => setAddStatus(s => ({ ...s, [selectedProduct.id]: 'idle' })), 2000);
    }
  };

  const handleOpenReviews = (product) => {
    setSelectedProductForReviews(product);
    setShowReviewsDrawer(true);
  };

  const handleCloseReviews = () => {
    setShowReviewsDrawer(false);
    setSelectedProductForReviews(null);
  };

  const toggleFilter = (filterType, value) => {
    switch (filterType) {
      case 'cuisine':
        setSelectedCuisines(prev => 
          prev.includes(value) 
            ? prev.filter(c => c !== value)
            : [...prev, value]
        );
        break;
      case 'dietary':
        setSelectedDietary(prev => 
          prev.includes(value) 
            ? prev.filter(d => d !== value)
            : [...prev, value]
        );
        break;
      case 'availability':
        setSelectedAvailability(prev => 
          prev.includes(value) 
            ? prev.filter(a => a !== value)
            : [...prev, value]
        );
        break;
    }
  };

  const clearFilters = () => {
    setSelectedCuisines([]);
    setSelectedDietary([]);
    setSelectedAvailability([]);
    setSortBy('rating');
  };

  // Helper to get current quantity in cart
  const getCartQty = (itemId) => {
    const found = cartItems.find(i => i.id === itemId);
    return found ? found.qty : 0;
  };

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG, paddingBottom: 'calc(120px + env(safe-area-inset-bottom) + 56px)' }}>
      {/* Header with Search and Location */}
      <header style={{ 
        background: NEUTRAL_WHITE, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
        position: 'sticky', 
        top: 0, 
        zIndex: 100 
      }}>
        <div style={{ padding: '16px 20px' }}>
          {/* Location */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <Location set="regular" size={16} primaryColor={PRIMARY_BROWN} />
            <span style={{ marginLeft: 8, fontSize: 12, color: NEUTRAL_TEXT, ...bodyFont }}>
              {locationDisplay}
            </span>
          </div>
          
          {/* Search Bar and Filter Button Row */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12,
            marginBottom: 16
          }}>
            {/* Search Bar */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: NEUTRAL_BG, 
              borderRadius: 10, 
              padding: '8px 12px',
              flex: 1
            }}>
              <Search set="regular" size={16} primaryColor={ACCENT_GRAY} />
              <input
                type="text"
                placeholder="Search for food or vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  marginLeft: 8,
                  fontSize: 12,
                  color: NEUTRAL_TEXT,
                  width: '100%',
                  outline: 'none',
                  ...bodyFont
                }}
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: NEUTRAL_WHITE,
                color: NEUTRAL_TEXT,
                border: 'none',
                borderRadius: 10,
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 40,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
                ...bodyFont
              }}
            >
              <Filter set="regular" size={14} primaryColor={ACCENT_GRAY} />
              Filters
            </button>
          </div>

          {/* Category Tabs */}
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6 }}>
            {/* Date Selector Button - Leftmost Position */}
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              style={{
                background: showDatePicker ? PRIMARY_BROWN : NEUTRAL_WHITE,
                color: showDatePicker ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                border: 'none',
                borderRadius: 16,
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 36,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
                ...bodyFont
              }}
            >
              Available: {getDisplayTextForPeriod(selectedTimePeriod, selectedDate)}
            </button>
            
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                style={{
                  background: selectedCategory === category.id ? category.color : NEUTRAL_WHITE,
                  color: selectedCategory === category.id ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                  border: 'none',
                  borderRadius: 16,
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 36,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease',
                  ...bodyFont
                }}
              >
                {category.name}
              </button>
            ))}
          </div>


        </div>
      </header>

      {/* Filter Panel */}
      {showFilters && (
        <div style={{ 
          background: NEUTRAL_WHITE, 
          borderBottom: `1px solid ${NEUTRAL_BG}`,
          padding: '20px'
        }}>
          {/* Sort Options */}
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 12 }}>Sort By</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {sortOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => setSortBy(option.id)}
                  style={{
                    background: sortBy === option.id ? PRIMARY_BROWN : NEUTRAL_BG,
                    color: sortBy === option.id ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                    border: 'none',
                    borderRadius: 16,
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: 32,
                    ...bodyFont
                  }}
                >
                  {option.name}
                </button>
              ))}
            </div>
          </div>

          {/* Cuisine Filter */}
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 12 }}>Cuisine</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {cuisines.slice(0, 8).map(cuisine => (
                <button
                  key={cuisine}
                  onClick={() => toggleFilter('cuisine', cuisine)}
                  style={{
                    background: selectedCuisines.includes(cuisine) ? PRIMARY_BROWN : NEUTRAL_BG,
                    color: selectedCuisines.includes(cuisine) ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                    border: 'none',
                    borderRadius: 16,
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: 32,
                    ...bodyFont
                  }}
                >
                  {cuisine}
                </button>
              ))}
            </div>
          </div>

          {/* Dietary Filter */}
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 12 }}>Dietary</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {dietaryTags.slice(0, 6).map(diet => (
                <button
                  key={diet}
                  onClick={() => toggleFilter('dietary', diet)}
                  style={{
                    background: selectedDietary.includes(diet) ? PRIMARY_BROWN : NEUTRAL_BG,
                    color: selectedDietary.includes(diet) ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                    border: 'none',
                    borderRadius: 16,
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: 32,
                    ...bodyFont
                  }}
                >
                  {diet}
                </button>
              ))}
            </div>
          </div>

          {/* Availability Filter */}
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, marginBottom: 12 }}>Availability</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {availabilityOptions.map(availability => (
                <label key={availability} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedAvailability.includes(availability)}
                    onChange={() => toggleFilter('availability', availability)}
                    style={{ marginRight: 8 }}
                  />
                  <span style={{ fontSize: 14, color: NEUTRAL_TEXT, ...bodyFont }}>{availability}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Clear Filters */}
          <button
            onClick={clearFilters}
            style={{
              background: NEUTRAL_BG,
              color: ACCENT_GRAY,
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              minHeight: 44,
              ...bodyFont
            }}
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDatePicker(false);
            }
          }}
        >
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 16,
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ ...headingFont, fontSize: 18, color: NEUTRAL_TEXT, margin: 0 }}>
                Select Available Time Period
              </h3>
              <button
                onClick={() => setShowDatePicker(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: ACCENT_GRAY,
                  padding: 0,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Time Period Options */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ ...headingFont, fontSize: 14, color: NEUTRAL_TEXT, marginBottom: '12px' }}>
                Quick Options
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { id: 'today', label: 'Today' },
                  { id: 'this-week', label: 'This Week' },
                  { id: 'next-week', label: 'Next Week' },
                  { id: 'this-month', label: 'This Month' }
                ].map(option => (
                  <button
                    key={option.id}
                    onClick={() => setSelectedTimePeriod(selectedTimePeriod === option.id ? null : option.id)}
                    style={{
                      background: selectedTimePeriod === option.id ? PRIMARY_BROWN : NEUTRAL_BG,
                      color: selectedTimePeriod === option.id ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 12px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      textAlign: 'left',
                      ...bodyFont
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Custom Date Selection */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ ...headingFont, fontSize: 14, color: NEUTRAL_TEXT, marginBottom: '12px' }}>
                Custom Date
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 14,
                  color: NEUTRAL_TEXT,
                  ...bodyFont
                }}>
                  <input
                    type="radio"
                    checked={selectedTimePeriod === 'custom'}
                    onChange={() => setSelectedTimePeriod('custom')}
                    style={{ marginRight: 8 }}
                  />
                  Specific Date
                </label>
              </div>
              
              {selectedTimePeriod === 'custom' && (
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]} // Can't select past dates
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: `2px solid ${NEUTRAL_BG}`,
                    borderRadius: 8,
                    fontSize: 16,
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                />
              )}
            </div>
            
            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'space-between'
            }}>
              <button
                onClick={() => {
                  setSelectedTimePeriod(null);
                  setSelectedDate(new Date().toISOString().split('T')[0]);
                }}
                style={{
                  background: NEUTRAL_BG,
                  color: ACCENT_GRAY,
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  ...bodyFont
                }}
              >
                Clear All
              </button>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => setShowDatePicker(false)}
                  style={{
                    background: NEUTRAL_BG,
                    color: NEUTRAL_TEXT,
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 16px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    ...bodyFont
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowDatePicker(false);
                    // The date is already updated via the input onChange
                  }}
                  style={{
                    background: PRIMARY_BROWN,
                    color: NEUTRAL_WHITE,
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 16px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    ...bodyFont
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Food Items Grid */}
      <main style={{ padding: '20px' }}>
        {/* Loading State */}
        {loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: ACCENT_GRAY,
            fontSize: 16,
            ...bodyFont
          }}>
            Loading nearby products...
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px',
            color: '#F44336',
            fontSize: 14,
            background: '#FFEBEE',
            borderRadius: 8,
            margin: '0 20px 20px 20px',
            ...bodyFont
          }}>
            {error}
          </div>
        )}

        {/* Distance Filter Info */}
        {!loading && !error && filterInfo.userLocation && (
          <div style={{ 
            textAlign: 'center', 
            padding: '10px 20px',
            color: ACCENT_GRAY,
            fontSize: 14,
            background: NEUTRAL_WHITE,
            borderRadius: 8,
            margin: '0 20px 20px 20px',
            ...bodyFont
          }}>
            Showing products within {filterInfo.distancePreference} miles of your location
          </div>
        )}

        {/* Food Items Grid */}
        {!loading && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                          gap: 24 
          }}>
            {filteredFoodItems.map(item => (
              <div 
                key={item.id} 
                onClick={() => handleProductClick(item)}
                style={{ 
                  background: NEUTRAL_WHITE, 
                  borderRadius: 0, 
                  overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer',
                  ':hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
                  }
                }}
              >
                {/* Food Video - 9:16 Aspect Ratio */}
                <div style={{ 
                  position: 'relative', 
                  width: '100%',
                  aspectRatio: '9/16',
                  background: '#000'
                }}>
                  {/* Video Player */}
                  <video
                    ref={(el) => {
                      if (el) {
                        const itemIndex = filteredFoodItems.findIndex(food => food.id === item.id);
                        if (itemIndex !== -1) {
                          // Direct assignment to prevent infinite loops
                          videoRefs[itemIndex] = el;
                          // Video ref set for item
                        }
                      }
                    }}
                    src={item.videoUrl || item.image} // Fallback to image if no video
                    poster={item.image} // Use image as poster/thumbnail
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      backgroundColor: '#000',
                      opacity: currentPlayingIndex === filteredFoodItems.findIndex(food => food.id === item.id) ? '1' : '0.7'
                    }}
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedData={(e) => {
                      // Set opacity when video is loaded
                      if (item.videoUrl) {
                        e.target.style.opacity = '1';
                      }
                    }}
                  />
                  
                  {/* Play Button Overlay - Show for non-video items or when video is not playing */}
                  {(!item.videoUrl || currentPlayingIndex !== filteredFoodItems.findIndex(food => food.id === item.id)) && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'rgba(0,0,0,0.6)',
                      borderRadius: '50%',
                      width: 30,
                      height: 30,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none'
                    }}>
                      <div style={{
                        width: 0,
                        height: 0,
                        borderLeft: '8px solid white',
                        borderTop: '5px solid transparent',
                        borderBottom: '5px solid transparent',
                        marginLeft: 2
                      }} />
                    </div>
                  )}
                  
                  {/* Availability Chip */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '20px',
                      right: '20px',
                      zIndex: 1000,
                      background: 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '8px',
                      fontWeight: 500,
                      color: '#333',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                      backdropFilter: 'blur(4px)',
                      border: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      // Calculate next available day based on product inventory
                      const today = new Date();
                      const todayString = today.toISOString().split('T')[0];
                      
                      // Use inventory data from the product object
                      if (item.inventory) {
                        const inventory = item.inventory;
                        
                        // Check if same-day orders are allowed
                        const sameDayAllowed = inventory.policy?.same_day_allowed || false;
                        
                        // Check if today has available inventory
                        const todayInventory = inventory.scheduled?.find(row => 
                          row.service_date === todayString && row.enabled && row.available_total > 0
                        );
                        
                        // Also check for dates within 1 day of today (timezone handling)
                        const todayPlusOne = new Date(today);
                        todayPlusOne.setDate(today.getDate() + 1);
                        const tomorrowString = todayPlusOne.toISOString().split('T')[0];
                        
                        const todayPlusOneInventory = inventory.scheduled?.find(row => 
                          row.service_date === tomorrowString && row.enabled && row.available_total > 0
                        );
                        
                        // Check if same-day orders are actually possible considering min_order_ahead policy
                        const policy = item.policy;
                        const canOrderToday = sameDayAllowed && 
                          (todayInventory || todayPlusOneInventory) &&
                          (!policy?.min_order_ahead || policy.min_order_ahead.value === 0);
                        
                        if (canOrderToday) {
                          // Show "Available today" only if same-day orders are allowed, inventory is available, AND no min_order_ahead restriction
                          return 'Available today';
                        }
                        
                        // Find the next available day with inventory
                        if (inventory.scheduled && Array.isArray(inventory.scheduled)) {
                          const todayString = today.toISOString().split('T')[0];
                          const policy = item.policy;
                          
                          const nextAvailableDay = inventory.scheduled
                            .filter(row => {
                              // Check if date has available units
                              if (row.available_total <= 0) return false;
                              
                              // Check if date is in the future
                              if (row.service_date <= todayString) return false;
                              
                              // Check policy restrictions
                              if (policy) {
                                const orderDate = new Date(row.service_date);
                                const daysDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
                                
                                // Check min_order_ahead policy
                                if (policy.min_order_ahead && policy.min_order_ahead.value) {
                                  const minDays = policy.min_order_ahead.value;
                                  if (daysDiff < minDays) return false;
                                }
                                
                                // Check same_day_allowed policy
                                if (!policy.same_day_allowed && daysDiff === 0) return false;
                              }
                              
                              return true;
                            })
                            .sort((a, b) => new Date(a.service_date) - new Date(b.service_date))
                            .find(row => new Date(row.service_date) > today);
                          
                          if (nextAvailableDay) {
                            const nextDate = new Date(nextAvailableDay.service_date);
                            const options = { weekday: 'short', month: 'short', day: 'numeric' };
                            const formattedDate = nextDate.toLocaleDateString('en-US', options);
                            return `Available: ${formattedDate}`;
                          }
                        }
                      }
                      
                      // Fallback: show tomorrow's date
                      const tomorrow = new Date(today);
                      tomorrow.setDate(today.getDate() + 1);
                      const options = { weekday: 'short', month: 'short', day: 'numeric' };
                      const nextAvailableDay = tomorrow.toLocaleDateString('en-US', options);
                      
                      return `Available: ${nextAvailableDay}`;
                    })()}
                  </div>
                </div>

                {/* Food Info */}
                <div style={{ padding: '12px' }}>
                  {/* Top Row: Name and Price */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: 4
                  }}>
                    <h3 style={{ 
                      ...headingFont, 
                      fontSize: 16, 
                      color: NEUTRAL_TEXT, 
                      margin: 0,
                      lineHeight: 1.2,
                      flex: 1
                    }}>
                      {item.name}
                    </h3>
                    <span style={{ 
                      ...headingFont, 
                      fontSize: 16, 
                      color: NEUTRAL_TEXT,
                      fontWeight: 600,
                      marginLeft: 6
                    }}>
                      ${item.price}
                    </span>
                  </div>
                  
                  {/* Creator and Rating Row */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-start', 
                    alignItems: 'center',
                    marginBottom: 6
                  }}>
                    {item.rating && typeof item.rating === 'number' && !isNaN(item.rating) && item.rating > 0 ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenReviews(item);
                        }}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        <Star set="bold" size={12} primaryColor="#FFD700" />
                        <span style={{ fontSize: 12, color: NEUTRAL_TEXT, fontWeight: 600 }}>
                          {item.rating.toFixed(1)}
                        </span>
                        {item.reviews > 0 && (
                          <span style={{ fontSize: 10, color: ACCENT_GRAY }}>
                            ({item.reviews})
                          </span>
                        )}
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: ACCENT_GRAY }}>
                        No reviews yet
                      </span>
                    )}
                  </div>

                  {/* Price and Add to Cart */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    {getCartQty(item.id) === 0 ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(item);
                        }}
                        style={{
                          background: addStatus[item.id] === 'added' ? SUCCESS_GREEN : ORANGE_ACCENT,
                          color: NEUTRAL_WHITE,
                          border: 'none',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: addStatus[item.id] === 'adding' ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          minHeight: 32,
                          opacity: addStatus[item.id] === 'adding' ? 0.7 : 1,
                          ...bodyFont
                        }}
                        disabled={addStatus[item.id] === 'adding'}
                        aria-label={addStatus[item.id] === 'added' ? 'Added to cart' : 'Add to cart'}
                      >
                        {addStatus[item.id] === 'added' ? (
                          <span style={{ fontSize: 14, lineHeight: 1 }}>✔</span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}>
                            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
                            <span>Quick add</span>
                          </div>
                        )}
                      </button>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: ORANGE_ACCENT, borderRadius: 8, padding: '8px 12px', width: '100%', justifyContent: 'center', minHeight: '32px' }}>
                        {/* Decrement or Trash */}
                        {getCartQty(item.id) > 1 ? (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              updateCartItem(item.id, getCartQty(item.id) - 1);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: NEUTRAL_WHITE,
                              fontSize: 16,
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              borderRadius: 4
                            }}
                            aria-label="Decrease quantity"
                          >-</button>
                        ) : (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              removeCartItem(item.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: NEUTRAL_WHITE,
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              borderRadius: 4
                            }}
                            aria-label="Remove from cart"
                          >
                            {/* Trash can icon, white */}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18" />
                              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                        )}
                        {/* Quantity */}
                        <span style={{ color: NEUTRAL_WHITE, fontWeight: 600, fontSize: 12, minWidth: 16, textAlign: 'center' }}>{getCartQty(item.id)}</span>
                        {/* Increment */}
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            updateCartItem(item.id, getCartQty(item.id) + 1);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: NEUTRAL_WHITE,
                            fontSize: 16,
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            borderRadius: 4
                          }}
                          aria-label="Increase quantity"
                        >+</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && !error && filteredFoodItems.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            color: ACCENT_GRAY
          }}>
            <h3 style={{ ...headingFont, fontSize: 20, marginBottom: 8 }}>
              No food items found
            </h3>
            <p style={{ fontSize: 16, ...bodyFont }}>
              Try adjusting your filters or search terms
            </p>
          </div>
        )}
      </main>

      <BottomNavigation />

      {/* Variant Selection Sheet */}
      {showVariantSheet && selectedProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            width: '100%',
            maxHeight: '80vh',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            padding: '20px',
            overflowY: 'auto',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
              paddingBottom: 15,
              borderBottom: '1px solid #eee'
            }}>
              <h2 style={{ ...headingFont, fontSize: 20, margin: 0 }}>
                {selectedProduct.name}
              </h2>
              <button
                onClick={() => {
                  setShowVariantSheet(false);
                  setSelectedProduct(null);
                  setVariantSelections({});
                  setQuantity(1);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            {/* Product Info */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <img
                  src={selectedProduct.imageUrl || selectedProduct.image}
                  alt={selectedProduct.name}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    objectFit: 'cover'
                  }}
                />
                <div>
                  <div style={{ ...headingFont, fontSize: 16, marginBottom: 4 }}>
                    {selectedProduct.name}
                  </div>
                  <div style={{ ...bodyFont, fontSize: 14, color: '#666' }}>
                    ${selectedProduct.price}
                  </div>
                </div>
              </div>
            </div>

            {/* Variants */}
            {(selectedProduct.variants || selectedProduct.variations || selectedProduct.sizes || selectedProduct.spiceLevels || []).map((variant, index) => (
              <div key={index} style={{ marginBottom: 20 }}>
                <h3 style={{ ...headingFont, fontSize: 16, marginBottom: 10 }}>
                  {variant.name || variant.key}
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(variant.options || []).map((option, optionIndex) => (
                    <button
                      key={optionIndex}
                      onClick={() => handleVariantSelection(variant.key || variant.name, option.label || option.value)}
                      style={{
                        background: variantSelections[variant.key || variant.name] === (option.label || option.value) 
                          ? 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)' 
                          : '#f5f5f5',
                        color: variantSelections[variant.key || variant.name] === (option.label || option.value) 
                          ? 'white' 
                          : '#333',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontSize: 14,
                        cursor: 'pointer',
                        ...bodyFont
                      }}
                    >
                      {option.label || option.value}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Quantity */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ ...headingFont, fontSize: 16, marginBottom: 10 }}>
                Quantity
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  style={{
                    background: '#f5f5f5',
                    border: 'none',
                    borderRadius: 8,
                    width: 40,
                    height: 40,
                    fontSize: 18,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  -
                </button>
                <span style={{ ...headingFont, fontSize: 16, minWidth: 30, textAlign: 'center' }}>
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  style={{
                    background: '#f5f5f5',
                    border: 'none',
                    borderRadius: 8,
                    width: 40,
                    height: 40,
                    fontSize: 18,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCartWithVariants}
              disabled={!allVariantsSelected() || addStatus[selectedProduct.id] === 'adding'}
              style={{
                width: '100%',
                background: allVariantsSelected() 
                  ? (addStatus[selectedProduct.id] === 'added' ? '#10B981' : 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)')
                  : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                padding: '16px',
                fontSize: 16,
                fontWeight: 600,
                cursor: allVariantsSelected() && addStatus[selectedProduct.id] !== 'adding' ? 'pointer' : 'not-allowed',
                ...headingFont
              }}
            >
              {addStatus[selectedProduct.id] === 'added' ? (
                'Added to Cart ✓'
              ) : addStatus[selectedProduct.id] === 'adding' ? (
                'Adding...'
              ) : allVariantsSelected() ? (
                `Add to Cart - $${(selectedProduct.price * quantity).toFixed(2)}`
              ) : (
                'Select Options First'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Product Reviews Drawer */}
      <ProductReviewsDrawer
        isOpen={showReviewsDrawer}
        onClose={handleCloseReviews}
        product={selectedProductForReviews}
        productRating={selectedProductForReviews?.rating || 0}
        productReviewCount={selectedProductForReviews?.reviews || 0}
      />
    </div>
  );
}

export default FoodBrowsing; 